import React, { useEffect, useState } from 'react';
import { Sparkles, X, Flame, HandHeart, ScrollText, Share2, CalendarClock } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { messagesApi } from '../services/messagesApi';
import { renderPulseRecapImage } from '../services/postsApi';

const DISMISS_KEY = 'lumera_pulse_dismissed';
const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

const CommunityPulseCard = ({ language = 'en', user }) => {
  const es = language === 'es';
  const [pulse, setPulse] = useState(null);
  const [dismissed, setDismissed] = useState(true);
  const [autoOn, setAutoOn] = useState(false);

  useEffect(() => {
    if (!user) return;
    const last = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
    if (Date.now() - last < WEEK_MS) return;
    let cancelled = false;
    messagesApi.pulse().then((p) => {
      if (cancelled) return;
      const hasContent = (p?.new_testimonies || 0) > 0 || (p?.answered_prayers || 0) > 0 || (p?.top_streaks || []).length > 0;
      if (hasContent) { setPulse(p); setDismissed(false); }
    }).catch(() => {});
    messagesApi.recapStatus().then((s) => { if (!cancelled) setAutoOn(!!s?.enabled); }).catch(() => {});
    return () => { cancelled = true; };
  }, [user]);

  const toggleAuto = async (v) => {
    setAutoOn(v);
    try {
      await messagesApi.recapOptin(v);
      toast.success(v
        ? (es ? 'Te mostraremos una vista previa cada semana antes de compartir 🙌' : "We'll show you a preview each week before sharing 🙌")
        : (es ? 'Vista previa semanal desactivada' : 'Weekly preview turned off'));
    } catch { setAutoOn(!v); }
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDismissed(true);
  };

  const goTestimonies = () => {
    const el = document.querySelector('[data-testid="testimonies-reel"]');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };
  const goAnsweredPrayers = () => {
    window.dispatchEvent(new CustomEvent('lumera:open-messages'));
  };
  const goStreaks = () => {
    window.dispatchEvent(new CustomEvent('lumera:open-messages', { detail: { focus: 'leaderboard' } }));
  };
  const shareRecap = async () => {
    const blob = await renderPulseRecapImage(pulse, es);
    window.dispatchEvent(new CustomEvent('lumera:milestone-share', { detail: {
      kind: 'pulse',
      direct: true,
      imageBlob: blob,
      message: es ? '¿Compartir el resumen semanal?' : "Share this week's recap?",
    }}));
  };

  if (dismissed || !pulse) return null;

  const top = pulse.top_streaks || [];

  return (
    <div
      className="relative mb-6 rounded-2xl border border-purple-500/30 bg-gradient-to-br from-purple-600/15 via-fuchsia-500/10 to-rose-500/15 p-5 overflow-hidden"
      data-testid="community-pulse-card"
    >
      <button
        onClick={dismiss}
        className="absolute top-3 right-3 text-muted-foreground hover:text-foreground transition-colors"
        aria-label={es ? 'Descartar' : 'Dismiss'}
        data-testid="community-pulse-dismiss"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="flex items-center gap-2 mb-4">
        <div className="flex items-center justify-center w-9 h-9 rounded-full bg-gradient-to-br from-purple-500 to-rose-500 text-white shrink-0">
          <Sparkles className="w-4.5 h-4.5" />
        </div>
        <div className="leading-tight">
          <h3 className="text-base font-bold">{es ? 'Pulso de la comunidad' : 'Community Pulse'}</h3>
          <p className="text-[11px] text-muted-foreground">{es ? 'Lo que pasó esta semana' : 'What happened this week'}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <button onClick={goTestimonies} className="rounded-xl bg-background/50 p-3 flex items-center gap-3 text-left transition-colors hover:bg-background/80" data-testid="community-pulse-testimonies">
          <ScrollText className="w-5 h-5 text-amber-500 shrink-0" />
          <div className="leading-tight">
            <p className="text-xl font-bold">{pulse.new_testimonies}</p>
            <p className="text-[11px] text-muted-foreground">{es ? 'Testimonios nuevos' : 'New testimonies'}</p>
          </div>
        </button>
        <button onClick={goAnsweredPrayers} className="rounded-xl bg-background/50 p-3 flex items-center gap-3 text-left transition-colors hover:bg-background/80" data-testid="community-pulse-answered">
          <HandHeart className="w-5 h-5 text-rose-500 shrink-0" />
          <div className="leading-tight">
            <p className="text-xl font-bold">{pulse.answered_prayers}</p>
            <p className="text-[11px] text-muted-foreground">{es ? 'Oraciones respondidas' : 'Answered prayers'}</p>
          </div>
        </button>
      </div>

      {top.length > 0 && (
        <button onClick={goStreaks} className="w-full rounded-xl bg-background/50 p-3 text-left transition-colors hover:bg-background/80" data-testid="community-pulse-streaks">
          <div className="flex items-center gap-1.5 mb-2">
            <Flame className="w-4 h-4 text-orange-500" />
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{es ? 'Rachas destacadas' : 'Top streaks'}</p>
          </div>
          <div className="space-y-2">
            {top.map((s, i) => (
              <div key={s.user_id} className="flex items-center gap-2.5" data-testid={`community-pulse-streak-${i}`}>
                <span className="text-xs font-bold w-4 text-muted-foreground">{i + 1}</span>
                <Avatar className="w-7 h-7">
                  {s.picture ? <AvatarImage src={s.picture} /> : null}
                  <AvatarFallback className="text-[10px] bg-purple-500/15">{(s.name || '?').slice(0, 2).toUpperCase()}</AvatarFallback>
                </Avatar>
                <p className="text-sm flex-1 truncate">{s.name}</p>
                <p className="text-sm font-semibold">🔥 {s.current_streak}</p>
              </div>
            ))}
          </div>
        </button>
      )}

      <button
        onClick={shareRecap}
        className="mt-3 w-full flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-purple-600 to-rose-500 text-white py-2.5 text-sm font-semibold transition-opacity hover:opacity-90"
        data-testid="community-pulse-share"
      >
        <Share2 className="w-4 h-4" />
        {es ? 'Compartir esta semana' : 'Share this week'}
      </button>

      <label className="mt-2 flex items-center gap-2 rounded-xl bg-background/40 px-3 py-2 cursor-pointer" data-testid="community-pulse-autoshare">
        <CalendarClock className="w-4 h-4 text-muted-foreground shrink-0" />
        <span className="text-[12px] leading-tight flex-1 text-muted-foreground">
          {es ? 'Vista previa de mi resumen cada semana antes de compartir' : 'Preview my recap each week before sharing'}
        </span>
        <Switch checked={autoOn} onCheckedChange={toggleAuto} data-testid="community-pulse-autoshare-toggle" />
      </label>
    </div>
  );
};

export default CommunityPulseCard;
